import { APIErrorNotOk } from '~/src/utils/error';
import {
  getAuthTokenUrl,
  getQuestionnaireSubmissionById,
  getQuestionnaireSubmissionListUrl,
} from '~/src/entities/questionnaires/urls';
import {
  type QuestionnaireSubmission,
  type Document,
  type QuestionSubmissionType,
  getQuestionSubmissionResponse,
  getQuestionVersionFromQuestionSubmission,
  getQuestionSubmissionSelectedOptions,
  Field,
  QuestionVersion,
  SectionVersion,
} from '@clio/questionnaire-builder';
import { isValid, parse, parseISO } from 'date-fns';
import { getFormattedDateFromIsoString } from '~/src/utils/date';
import { SubmissionQueryParams } from '~/src/entities/questionnaires/types';

export const getClioQuestionnaireAuthToken = async (orgFprint: string) => {
  const response = await fetch(getAuthTokenUrl(orgFprint), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new APIErrorNotOk(response);
  }

  const json = await response.json();

  return json.questionnaire_auth_token;
};

export const questionnaireAuthorizationHeaders = (token: string) => ({
  questionnaire_auth_token: token,
});

export const getDocumentMaps = (
  documents: Document[],
  canUseNormalizedFieldsAsKeys = false,
): { fields: Map<string, Field>; normalizedFields: Map<string, string> } => {
  const documentFields = new Map<string, Field>();
  const normalizedFields = new Map<string, string>();

  for (const document of documents) {
    for (const fieldGroup of Object.values(document.field_groups)) {
      for (const field of Object.values(fieldGroup.fields)) {
        const normalizedFieldKey = `${fieldGroup.normalized_field_group}.${field.normalized_field}`;
        documentFields.set(normalizedFieldKey, field);
        documentFields.set(field.field_group_field_id, field);

        normalizedFields.set(field.field_group_field_id, normalizedFieldKey);
        if (canUseNormalizedFieldsAsKeys) {
          normalizedFields.set(normalizedFieldKey, normalizedFieldKey);
        }
      }
    }
  }

  return { fields: documentFields, normalizedFields };
};

const convertToArrayIfPossible = (element: string) => {
  try {
    const newArrayObject = JSON.parse(element);

    if (Array.isArray(newArrayObject)) {
      return newArrayObject;
    }

    return [];
  } catch (error) {
    return element.split(',').map((item: string) => item.trim());
  }
};

export const getQuestionnaireQuestionArray = (
  questionnaireSubmission: QuestionnaireSubmission,
) => {
  const questionnaireQuestionArray: {
    [key: string]: any;
  }[] = [];

  const questionSubmissions =
    questionnaireSubmission.section_submissions_attributes.flatMap(
      (sectionSubmission) => sectionSubmission.question_submissions_attributes,
    );

  questionSubmissions.forEach((questionSubmission) => {
    const questionVersion = getQuestionVersionFromQuestionSubmission(
      questionnaireSubmission.questionnaire_version,
      questionSubmission,
    );

    const questionSubmissionType: QuestionSubmissionType =
      questionSubmission.question_submission_type;

    let responseLabel: string | null = '';
    if (questionVersion !== undefined) {
      switch (questionSubmissionType) {
        case 'QuestionSubmissionShort':
        case 'QuestionSubmissionLong': {
          responseLabel = questionSubmission.response_text;
          break;
        }
        case 'QuestionSubmissionDate': {
          responseLabel = questionSubmission.response_date;
          break;
        }
        case 'QuestionSubmissionMultipleChoiceSingle': {
          const selectedOptions = getQuestionSubmissionSelectedOptions(
            questionVersion,
            questionSubmission,
          );
          responseLabel =
            selectedOptions && selectedOptions[0]
              ? selectedOptions[0].label
              : '';
          break;
        }
        case 'QuestionSubmissionMultipleChoiceMulti': {
          const selectedOptions = getQuestionSubmissionSelectedOptions(
            questionVersion,
            questionSubmission,
          );
          const optionLabelArray = selectedOptions.map((o) => o.label);
          responseLabel = JSON.stringify(optionLabelArray);
          break;
        }
      }
    }

    questionnaireQuestionArray.push({
      question_version_id: questionVersion!.id,
      question_id: questionVersion!.question_id,
      question: questionVersion!.title,
      response: responseLabel,
      questionSubmissionType,
      mapping_fields: questionVersion!.question_mapping_versions,
    });
  });

  return questionnaireQuestionArray;
};

// derive a map of tag -> response from the questionnaire submission
export const getNormalizedField2ResponseMap = (
  questionnaireSubmission: QuestionnaireSubmission,
  documents: Document[],
  canUseNormalizedFieldsAsKeys = false,
) => {
  const responseDict: { [key: string]: string } = {};

  const questionSubmissions =
    questionnaireSubmission.section_submissions_attributes.flatMap(
      (sectionSubmission) => sectionSubmission.question_submissions_attributes,
    );

  const { fields: documentFields, normalizedFields } = getDocumentMaps(
    documents,
    canUseNormalizedFieldsAsKeys,
  );

  questionSubmissions.forEach((questionSubmission) => {
    const questionVersion = getQuestionVersionFromQuestionSubmission(
      questionnaireSubmission.questionnaire_version,
      questionSubmission,
    );

    const questionSubmissionType: QuestionSubmissionType =
      questionSubmission.question_submission_type;

    if (questionVersion !== undefined) {
      switch (questionSubmissionType) {
        case 'QuestionSubmissionShort':
        case 'QuestionSubmissionLong':
        case 'QuestionSubmissionDate': {
          const response = getQuestionSubmissionResponse(questionSubmission);
          questionVersion.question_mapping_versions.forEach((mapping) => {
            const normalizedField = normalizedFields.get(
              mapping.external_field_identifier,
            );
            if (normalizedField) {
              if (questionSubmissionType == 'QuestionSubmissionDate') {
                handleDateFieldResponse(
                  normalizedField,
                  response,
                  documentFields,
                  responseDict,
                );
              } else {
                responseDict[normalizedField] = response ?? '';
              }
            }
          });
          break;
        }

        case 'QuestionSubmissionMultipleChoiceSingle': {
          const responseCollection: string[] = [];

          const selectedOptions = getQuestionSubmissionSelectedOptions(
            questionVersion,
            questionSubmission,
          );

          const questionMapping =
            questionVersion.question_mapping_versions.length > 0
              ? questionVersion.question_mapping_versions[0]
              : undefined;
          if (questionMapping === undefined) {
            break;
          }
          // tagStr is {normalized_entity}.{normalized_attribute}
          const tagStr = questionMapping.external_field_identifier;
          const field = getFieldWithMultipleChoiceOptions(
            tagStr,
            documentFields,
          );

          if (field) {
            // a single-select question maps a single-select field
            field.multiple_choice_options.forEach((lawyawOption) => {
              const selectedOption = selectedOptions.find((selectedOption) =>
                selectedOption.question_option_mapping_versions.some(
                  (optionMapping) =>
                    parseInt(optionMapping.external_field_identifier) ===
                    lawyawOption.id,
                ),
              );
              if (selectedOption !== undefined) {
                responseCollection.push(lawyawOption.value);
              }
            });
          } else {
            // a single-select question maps a text field
            if (selectedOptions && selectedOptions[0]) {
              responseCollection.push(selectedOptions[0].label);
            }
          }

          const normalizedField = normalizedFields.get(
            questionMapping.external_field_identifier,
          );
          if (normalizedField === undefined) {
            break;
          }

          responseDict[normalizedField] = responseCollection[0] ?? '';
          break;
        }

        case 'QuestionSubmissionMultipleChoiceMulti': {
          let responseCollection: string[] = [];

          const selectedOptions = getQuestionSubmissionSelectedOptions(
            questionVersion,
            questionSubmission,
          );

          const questionMapping =
            questionVersion.question_mapping_versions.length > 0
              ? questionVersion.question_mapping_versions[0]
              : undefined;
          if (questionMapping === undefined) {
            break;
          }
          // tagStr is {normalized_entity}.{normalized_attribute}
          const tagStr = questionMapping.external_field_identifier;
          const field = getFieldWithMultipleChoiceOptions(
            tagStr,
            documentFields,
          );

          if (field) {
            // a multi-select question maps a multi-select field
            field.multiple_choice_options.forEach((lawyawOption) => {
              const selectedOption = selectedOptions.find((selectedOption) =>
                selectedOption.question_option_mapping_versions.some(
                  (optionMapping) =>
                    parseInt(optionMapping.external_field_identifier) ===
                    lawyawOption.id,
                ),
              );
              if (selectedOption !== undefined) {
                responseCollection.push(lawyawOption.value);
              } else {
                // need to include empty strings for each option that was not selected specifically
                // for multiselect.  This is how Lawyaw's populate page works.
                responseCollection.push('');
              }
            });
          } else {
            // a multi-select question maps a text field
            responseCollection = selectedOptions.map(
              (selectedOptions) => selectedOptions.label,
            );
          }

          const normalizedField = normalizedFields.get(
            questionMapping.external_field_identifier,
          );
          if (normalizedField === undefined) {
            break;
          }

          responseDict[normalizedField] = responseCollection.join(', ');
          break;
        }
      }
    }
  });

  return responseDict;
};

export const getFieldId2ResponseMap = (
  questionnaireSubmission: QuestionnaireSubmission,
  documents: Document[],
) => {
  const responseDict: { [key: string]: string } = {};

  const questionSubmissions =
    questionnaireSubmission.section_submissions_attributes.flatMap(
      (sectionSubmission) => sectionSubmission.question_submissions_attributes,
    );

  const { fields: documentFields } = getDocumentMaps(documents);

  questionSubmissions.forEach((questionSubmission) => {
    const questionVersion = getQuestionVersionFromQuestionSubmission(
      questionnaireSubmission.questionnaire_version,
      questionSubmission,
    );

    const questionSubmissionType: QuestionSubmissionType =
      questionSubmission.question_submission_type;

    if (questionVersion !== undefined) {
      switch (questionSubmissionType) {
        case 'QuestionSubmissionShort':
        case 'QuestionSubmissionLong':
        case 'QuestionSubmissionDate': {
          const response = getQuestionSubmissionResponse(questionSubmission);

          questionVersion.question_mapping_versions.forEach((mapping) => {
            if (questionSubmissionType == 'QuestionSubmissionDate') {
              handleDateFieldResponse(
                mapping.external_field_identifier,
                response,
                documentFields,
                responseDict,
              );
            } else {
              responseDict[mapping.external_field_identifier] = response ?? '';
            }
          });
          break;
        }

        case 'QuestionSubmissionMultipleChoiceSingle': {
          const responseCollection: string[] = [];

          const selectedOptions = getQuestionSubmissionSelectedOptions(
            questionVersion,
            questionSubmission,
          );

          const questionMapping =
            questionVersion.question_mapping_versions.length > 0
              ? questionVersion.question_mapping_versions[0]
              : undefined;
          if (questionMapping === undefined) {
            break;
          }
          // tagStr is {Entity.id}.{Attribute.id} or {normalized_entity}.{normalized_attribute}
          const tagStr = questionMapping.external_field_identifier;
          const field = getFieldWithMultipleChoiceOptions(
            tagStr,
            documentFields,
          );

          if (field) {
            // a single-select question maps a single-select field
            field.multiple_choice_options.forEach((lawyawOption) => {
              const selectedOption = selectedOptions.find((selectedOption) =>
                selectedOption.question_option_mapping_versions.some(
                  (optionMapping) => {
                    return (
                      optionMapping.external_field_identifier ===
                        `templatetags_attributemultiplechoiceoption__id__${lawyawOption.id}` ||
                      optionMapping.external_field_identifier ===
                        lawyawOption.value
                    );
                  },
                ),
              );
              if (selectedOption !== undefined) {
                responseCollection.push(lawyawOption.value);
              }
            });
          } else {
            // a single-select question maps a text field
            if (selectedOptions && selectedOptions[0]) {
              responseCollection.push(selectedOptions[0].label);
            }
          }

          responseDict[questionMapping.external_field_identifier] =
            responseCollection[0] ?? '';
          break;
        }

        case 'QuestionSubmissionMultipleChoiceMulti': {
          let responseCollection: string[] = [];

          const selectedOptions = getQuestionSubmissionSelectedOptions(
            questionVersion,
            questionSubmission,
          );

          const questionMapping =
            questionVersion.question_mapping_versions.length > 0
              ? questionVersion.question_mapping_versions[0]
              : undefined;
          if (questionMapping === undefined) {
            break;
          }
          // tagStr is {Entity.id}.{Attribute.id} or {normalized_entity}.{normalized_attribute}
          const tagStr = questionMapping.external_field_identifier;
          const field = getFieldWithMultipleChoiceOptions(
            tagStr,
            documentFields,
          );

          if (field) {
            // a multi-select question maps a multi-select field
            field.multiple_choice_options.forEach((lawyawOption) => {
              const selectedOption = selectedOptions.find((selectedOption) =>
                selectedOption.question_option_mapping_versions.some(
                  (optionMapping) => {
                    return (
                      optionMapping.external_field_identifier ===
                        `templatetags_attributemultiplechoiceoption__id__${lawyawOption.id}` ||
                      optionMapping.external_field_identifier ===
                        lawyawOption.value ||
                      parseInt(optionMapping.external_field_identifier) ===
                        lawyawOption.id
                    );
                  },
                ),
              );
              if (selectedOption !== undefined) {
                responseCollection.push(lawyawOption.value);
              } else {
                // need to include empty strings for each option that was not selected specifically
                // for multiselect.  This is how Lawyaw's populate page works.
                responseCollection.push('');
              }
            });

            // stacked_save_data format is ["soccer","hockey","basketball","",""]
            responseDict[questionMapping.external_field_identifier] =
              JSON.stringify(responseCollection);
            break;
          } else {
            // a multi-select question maps a text field
            responseCollection = selectedOptions.map(
              (selectedOptions) => selectedOptions.label,
            );
            responseDict[questionMapping.external_field_identifier] =
              responseCollection.join(', ');
            break;
          }
        }

        case 'QuestionSubmissionCheckbox': {
          questionVersion.question_option_versions.forEach((optionVersion) => {
            optionVersion.question_option_mapping_versions.forEach(
              (mapping) => {
                const isFound =
                  questionSubmission.question_submission_selected_options.find(
                    (so) => optionVersion.id === so.question_option_version_id,
                  );
                if (isFound) {
                  responseDict[mapping.external_field_identifier] = 'Yes';
                }
              },
            );
          });
        }
      }
    }
  });

  return responseDict;
};

export const tryConvertToNumber = (str: string): number | string => {
  const num = Number(str);

  return isNaN(num) ? str : num;
};

// date-fns v2 parse formats matching the docx DateFormatSelect options
// plus backend DATE_ATTRIBUTE_FORMAT_CHOICES (yyyy-MM-dd, yy/MM/dd)
const FALLBACK_DATE_FORMATS = [
  'MM/dd/yyyy',
  'MM/dd/yy',
  'MM.dd.yyyy',
  'MM.dd.yy',
  'dd/MM/yyyy',
  'dd/MM/yy',
  'dd.MM.yyyy',
  'dd.MM.yy',
  'yyyy-MM-dd',
  'yy/MM/dd',
  'MMMM d, yyyy',
  'MMMM d',
  'd MMMM yyyy',
  'EEEE, MMMM d, yyyy',
  "do 'day of' MMMM, yyyy",
  'MMM. d, yy',
  'MMM. d',
  'MMM-d',
];

const parseDateValue = (value: string): Date => {
  const isoDate = parseISO(value);
  if (isValid(isoDate)) return isoDate;

  for (const fmt of FALLBACK_DATE_FORMATS) {
    const parsed = parse(value, fmt, new Date());
    if (isValid(parsed)) return parsed;
  }

  throw new Error(
    `Unable to parse date value: "${value}". Add the format to FALLBACK_DATE_FORMATS.`,
  );
};

export const createPrepopulateRecord = (
  v2Data: Record<string, unknown>,
  questionnaireSubmission: QuestionnaireSubmission,
) => {
  let externalFieldIdentifierVersionHash =
    createExternalFieldIdentifierVersionHash(questionnaireSubmission);
  let questionOptionVersionIdHash = createQuestionOptionVersionIdHash(
    questionnaireSubmission,
  );

  let updatedSectionSubmissionsAttributes: {
    section_version_id: number;
    question_submissions_attributes: {
      question_version_id: number;
      response_date?: string;
      response_text?: string;
      question_submission_selected_options_attributes?: {
        question_option_version_id: number;
      }[];
    }[];
  }[] = [];

  for (let [key, rawValue] of Object.entries(v2Data)) {
    if (!(key in externalFieldIdentifierVersionHash)) continue;

    let questionVersion = externalFieldIdentifierVersionHash[key];
    if (!questionVersion || rawValue == null) continue;

    // Skip plain objects — String({...}) yields "[object Object]" which would
    // surface as user-visible garbage in the prepopulated response.
    if (typeof rawValue === 'object' && !Array.isArray(rawValue)) continue;

    // v2Data is typed as a string map but the matter API can return numbers,
    // booleans, arrays (multi-select), etc. Coerce defensively so downstream
    // .trim / .split / parsing don't blow up. Arrays are JSON-stringified
    // (not comma-joined) so option labels containing commas round-trip
    // losslessly through convertToArrayIfPossible's JSON.parse path.
    const value: string = Array.isArray(rawValue)
      ? JSON.stringify(rawValue)
      : typeof rawValue === 'string'
      ? rawValue
      : String(rawValue);

    if (value.trim() === '') continue;

    let questionType = questionVersion.question_type;

    if (
      !updatedSectionSubmissionsAttributes.some(
        (section) =>
          section['section_version_id'] === questionVersion?.section_version_id,
      )
    ) {
      updatedSectionSubmissionsAttributes.push({
        section_version_id: questionVersion.section_version_id,
        question_submissions_attributes: [],
      });
    }

    updatedSectionSubmissionsAttributes.forEach((section) => {
      if (section['section_version_id'] !== questionVersion?.section_version_id)
        return;

      let response: {
        response_date?: string;
        response_text?: string;
        question_submission_selected_options_attributes?: {
          question_option_version_id: number;
        }[];
      } = {};

      switch (questionType) {
        case 'QuestionDate': {
          response['response_date'] = parseDateValue(value).toISOString();
          break;
        }
        case 'QuestionMultipleChoiceSingle': {
          let questionVersionId = questionVersion.question_version_id;
          let multipleChoiceVersion =
            questionOptionVersionIdHash[questionVersionId];

          if (!multipleChoiceVersion || !questionOptionVersionIdHash) return;

          const questionOptionVersionId = multipleChoiceVersion[value]?.id;

          if (!questionOptionVersionId) return;

          response['question_submission_selected_options_attributes'] = [
            {
              question_option_version_id: questionOptionVersionId,
            },
          ];
          break;
        }
        case 'QuestionMultipleChoiceMulti': {
          let questionVersionId = questionVersion.question_version_id;
          let multipleChoiceVersion =
            questionOptionVersionIdHash[questionVersionId];

          if (!multipleChoiceVersion || !questionOptionVersionIdHash) return;

          const item = multipleChoiceVersion;

          let result = convertToArrayIfPossible(value)
            .map((option) => {
              const questionOptionVersionId = item[option]?.id;
              if (!questionOptionVersionId) return null;

              return {
                question_option_version_id: questionOptionVersionId,
              };
            })
            .filter((option) => option !== null);

          if (!result || result.length === 0) return;

          response['question_submission_selected_options_attributes'] =
            result as {
              question_option_version_id: number;
            }[];

          break;
        }
        default: {
          response['response_text'] = value;
          break;
        }
      }

      section['question_submissions_attributes'].push({
        question_version_id: questionVersion?.question_version_id,
        ...response,
      });
    });
  }

  return updatedSectionSubmissionsAttributes;
};

export const createExternalFieldIdentifierVersionHash = (
  questionnaireSubmission: QuestionnaireSubmission,
) => {
  let externalFieldIdentifierVersionHash: Record<
    string,
    {
      question_version_id: number;
      section_version_id: number;
      question_type: string;
    }
  > = {};

  questionnaireSubmission.questionnaire_version.section_versions.forEach(
    (sectionVersion: SectionVersion) => {
      sectionVersion.question_versions.forEach(
        (questionVersion: QuestionVersion) => {
          questionVersion.question_mapping_versions.forEach(
            (questionMappingVersion) => {
              externalFieldIdentifierVersionHash[
                questionMappingVersion.external_field_identifier
              ] = {
                question_version_id: questionVersion.id,
                section_version_id: sectionVersion.id,
                question_type: questionVersion.question_type,
              };
            },
          );
        },
      );
    },
  );

  return externalFieldIdentifierVersionHash;
};

export const createQuestionOptionVersionIdHash = (
  questionnaireSubmission: QuestionnaireSubmission,
) => {
  let questionOptionVersionIdHash: Record<
    number,
    Record<string, { id: number }>
  > = {};

  questionnaireSubmission.questionnaire_version.section_versions.forEach(
    (sectionVersion: SectionVersion) => {
      sectionVersion.question_versions.forEach((questionVersion) => {
        let questionType = questionVersion.question_type;
        if (
          ![
            'QuestionMultipleChoiceSingle',
            'QuestionMultipleChoiceMulti',
          ].includes(questionType)
        )
          return;

        let externalFieldIdentifierMultipleChoiceVersionHash: Record<
          string,
          { id: number }
        > = {};
        questionVersion.question_option_versions.forEach(
          (questionOptionVersion) => {
            externalFieldIdentifierMultipleChoiceVersionHash[
              questionOptionVersion.label
            ] = {
              id: questionOptionVersion.id,
            };

            const key = questionVersion.id;

            if (!key) return;

            questionOptionVersionIdHash[key] =
              externalFieldIdentifierMultipleChoiceVersionHash;
          },
        );
      });
    },
  );

  return questionOptionVersionIdHash;
};

const handleDateFieldResponse = (
  field: string,
  response: string | null,
  documentFields: Map<string, Field>,
  responseDict: Record<string, string>,
) => {
  if (documentFields.get(field)?.type === 'QuestionDate') {
    responseDict[field] = response ?? '';
  } else {
    responseDict[field] = getFormattedDateFromIsoString(response ?? '');
  }
};

const getFieldWithMultipleChoiceOptions = (
  tagStr: string,
  documentFields: Map<string, Field>,
): Field | undefined => {
  const field = documentFields.get(tagStr);
  if (
    field &&
    field.multiple_choice_options &&
    field.multiple_choice_options.length > 0
  ) {
    return field;
  }
  return undefined;
};

export const deleteQuestionnaireSubmissionsForMatter = async (
  orgFprint: string,
  matterId: string,
) => {
  const params: Partial<SubmissionQueryParams> = {
    target_identifier: matterId,
  };
  const token = await getClioQuestionnaireAuthToken(orgFprint);
  const response = await fetch(getQuestionnaireSubmissionListUrl(params), {
    headers: questionnaireAuthorizationHeaders(token),
  });

  if (response.ok) {
    const submissions = await response.json();
    const deletePromises = submissions.map((qSub: QuestionnaireSubmission) =>
      fetch(getQuestionnaireSubmissionById(qSub.id.toString()), {
        method: 'DELETE',
        headers: {
          ...questionnaireAuthorizationHeaders(token),
        },
      }),
    );
    await Promise.all(deletePromises);
  }
};
